import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  Renderer2,
  RendererStyleFlags2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageService, ToastMessageOptions } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { ButtonDirective, ButtonVariants } from '../button.directive';

/** Durée d'affichage d'un toast info / succès (prototype notifications, 2026-10-05). */
export const WH_TOAST_LIFE_MS = 5000;
/** Nombre maximal de toasts empilés : au-delà, le plus ancien sort. */
export const WH_TOAST_MAX = 3;

/** Action proposée dans un toast, passée par `data.actions` du message. */
export interface WhToastAction {
  label: string;
  run: () => void;
  variant?: ButtonVariants['variant'];
}

/** Ce que `wh-toast` lit dans le champ `data` d'un `ToastMessageOptions`. */
export interface WhToastData {
  actions?: WhToastAction[];
  /** Toast « en cours » (posé par `toastProgress`) : spinner à la place de l'icône. */
  loading?: boolean;
}

type WhToastTone = 'info' | 'success' | 'warn' | 'error';

const TONE_LABEL: Record<WhToastTone, string> = {
  info: 'Info',
  success: 'Succès',
  warn: 'Attention',
  error: 'Problème',
};

function toneOf(m: ToastMessageOptions): WhToastTone {
  return m.severity === 'success' || m.severity === 'warn' || m.severity === 'error' ? m.severity : 'info';
}

/**
 * Règle de durée du toast unique : attention et problème restent jusqu'à une action (persistants,
 * même si l'appelant donnait une durée) ; info et succès partent après {@link WH_TOAST_LIFE_MS},
 * sauf durée explicite ou `sticky` de l'appelant (le toast « en cours » de `toastProgress`).
 * Une gravité inconnue (`secondary`, absente) est traitée comme info.
 */
export function whToastPolicy(m: ToastMessageOptions): ToastMessageOptions {
  const tone = toneOf(m);
  if (tone === 'warn' || tone === 'error') return { ...m, sticky: true };
  return m.sticky ? m : { ...m, life: m.life ?? WH_TOAST_LIFE_MS };
}

/**
 * LE toast des trois apps (inside, integrator, customer) — décision Eddy du 2026-10-05 : le
 * `MessageService` de PrimeNG reste le moteur, ce composant en est l'unique sortie, avec le
 * gabarit « WhToast » du prototype `docs/maquettes/notifications/prototype.html` : carte blanche
 * en bas à droite, icône de gravité en forme ET en couleur (info = rond bleu, attention = triangle
 * jaune, problème = octogone rouge, succès = rond vert), titre (`summary`), texte (`detail`),
 * actions optionnelles (`data.actions`, voir {@link WhToastData}).
 *
 * Monté UNE fois, au niveau de chaque app. Trois écarts assumés avec `<p-toast>` nu :
 * - **toutes les keys** sont affichées ici (`default`, `save`, `state`, les `wh-progress-<n>` de
 *   `toastProgress`, ou aucune) : les appelants n'ont pas à connaître l'emplacement ;
 * - `clear(key)` ne retire que les toasts de cette key (PrimeNG ne vide que le `<p-toast>` qui
 *   porte la key, et il n'y en a plus qu'un, sans key) ;
 * - {@link WH_TOAST_MAX} toasts au plus, et la durée suit {@link whToastPolicy}.
 * Ces écarts passent par les méthodes publiques `canAdd`/`add` du `Toast` PrimeNG 18 : le spec
 * de ce composant tourne sur le vrai `Toast` et casse si une montée de version les change.
 */
@Component({
  selector: 'wh-toast',
  standalone: true,
  imports: [Toast, ButtonDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Le conteneur et la boîte de chaque message sont rendus par PrimeNG, hors de notre vue :
  // on neutralise ici la boîte colorée d'Aura (styles non encapsulés, préfixés par .wh-toast).
  encapsulation: ViewEncapsulation.None,
  styles: `
    .p-toast.wh-toast {
      width: min(380px, calc(100vw - 2rem));
    }
    .wh-toast .p-toast-message {
      margin: 0 0 0.5rem 0;
      background: transparent;
      border: 0;
      border-radius: 0.75rem;
      box-shadow: none;
      backdrop-filter: none;
    }
  `,
  template: `
    <p-toast position="bottom-right" styleClass="wh-toast" [preventOpenDuplicates]="true">
      <ng-template #headless let-message let-closeFn="closeFn">
        <div
          data-testid="wh-toast"
          [attr.role]="tone(message) === 'warn' || tone(message) === 'error' ? 'alert' : 'status'"
          class="w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800">
          <div class="flex gap-3 px-4 py-3">
            <div class="pt-0.5">
              @if (data(message).loading) {
                <svg
                  data-testid="wh-toast-icon"
                  data-shape="spinner"
                  class="text-pacificBlue-500 size-5 shrink-0 animate-spin"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              } @else {
                @switch (tone(message)) {
                  @case ('warn') {
                    <svg
                      data-testid="wh-toast-icon"
                      data-shape="triangle"
                      class="size-5 shrink-0 text-yellow-400"
                      viewBox="0 0 24 24"
                      aria-hidden="true">
                      <path
                        d="M12 2.2L23 21.3H1z"
                        fill="currentColor"
                        stroke="currentColor"
                        stroke-width="1.6"
                        stroke-linejoin="round" />
                      <path
                        d="M12 9.2v5.6M12 18v.1"
                        class="stroke-gray-900"
                        stroke-width="2.4"
                        stroke-linecap="round" />
                    </svg>
                  }
                  @case ('error') {
                    <svg
                      data-testid="wh-toast-icon"
                      data-shape="octagon"
                      class="size-5 shrink-0 text-red-600"
                      viewBox="0 0 24 24"
                      aria-hidden="true">
                      <path d="M7.9 1.5h8.2l5.9 5.9v8.2l-5.9 5.9H7.9L2 15.6V7.4z" fill="currentColor" />
                      <path
                        d="M8.5 8.5l7 7M15.5 8.5l-7 7"
                        class="stroke-white"
                        stroke-width="2.4"
                        stroke-linecap="round" />
                    </svg>
                  }
                  @case ('success') {
                    <svg
                      data-testid="wh-toast-icon"
                      data-shape="circle"
                      class="size-5 shrink-0 text-green-500"
                      viewBox="0 0 24 24"
                      aria-hidden="true">
                      <circle cx="12" cy="12" r="10" fill="currentColor" />
                      <path
                        d="M7.5 12.5l3 3 6-6.5"
                        fill="none"
                        class="stroke-white"
                        stroke-width="2.4"
                        stroke-linecap="round"
                        stroke-linejoin="round" />
                    </svg>
                  }
                  @default {
                    <svg
                      data-testid="wh-toast-icon"
                      data-shape="circle"
                      class="text-pacificBlue-500 size-5 shrink-0"
                      viewBox="0 0 24 24"
                      aria-hidden="true">
                      <circle cx="12" cy="12" r="10" fill="currentColor" />
                      <path d="M12 11v6M12 7.2v.1" class="stroke-white" stroke-width="2.4" stroke-linecap="round" />
                    </svg>
                  }
                }
              }
              <span class="sr-only">{{ label(message) }}</span>
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-start justify-between gap-2">
                <div
                  data-testid="wh-toast-title"
                  class="whitespace-pre-line break-words text-sm font-bold leading-snug text-gray-900 dark:text-slate-100">
                  {{ message.summary || message.detail }}
                </div>
                @if (message.closable !== false) {
                  <button
                    type="button"
                    data-testid="wh-toast-close"
                    class="-mr-1 -mt-0.5 grid size-6 shrink-0 place-items-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100"
                    aria-label="Fermer"
                    (click)="closeFn($event)">
                    <svg class="size-3.5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    </svg>
                  </button>
                }
              </div>
              @if (message.summary && message.detail) {
                <div
                  data-testid="wh-toast-text"
                  class="mt-0.5 whitespace-pre-line break-words text-xs leading-relaxed text-gray-600 dark:text-slate-300">
                  {{ message.detail }}
                </div>
              }
              @if (data(message).actions?.length) {
                <div class="mt-2 flex flex-wrap items-center gap-1.5">
                  @for (action of data(message).actions; track action.label) {
                    <button
                      libBtn
                      type="button"
                      data-testid="wh-toast-action"
                      size="xxs"
                      [variant]="action.variant ?? 'outline'"
                      (click)="runAction(action, closeFn, $event)">
                      {{ action.label }}
                    </button>
                  }
                </div>
              }
            </div>
          </div>
          @if (message.sticky && message.closable !== false) {
            <div
              class="border-t border-gray-100 bg-gray-50 px-4 py-1 text-[11px] text-gray-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-400">
              Reste affiché jusqu'à une action ou la fermeture
            </div>
          }
        </div>
      </ng-template>
    </p-toast>
  `,
})
export class WhToastComponent implements OnInit, AfterViewInit {
  @ViewChild(Toast, { static: true }) private readonly toast!: Toast;

  /**
   * Au-dessus du bouton flottant « signaler un bug » (`bug-report-button` : `fixed bottom-4
   * right-4 h-12`, son bord haut est à 4 rem du bas) + 0,5 rem d'air, aligné sur son bord droit.
   * Même décalage partout, y compris dans inside qui n'a pas ce bouton : une seule position.
   * PrimeNG pose `bottom`/`right` (20px) en style inline via `ngStyle`, qui l'emporte sur son
   * input `style` : on réécrit ces deux propriétés sur le conteneur, en `!important`.
   */
  static readonly OFFSET = { bottom: '4.5rem', right: '1rem' } as const;

  readonly #messages = inject(MessageService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #renderer = inject(Renderer2);

  ngOnInit(): void {
    const toast = this.toast;
    // Une seule sortie : toutes les keys passent (PrimeNG n'accepte que `message.key === toast.key`).
    toast.canAdd = m => !(toast.preventOpenDuplicates && toast.containsMessage(toast.messages ?? [], m));
    toast.add = incoming => {
      toast.messages = [...(toast.messages ?? []), ...incoming.map(whToastPolicy)].slice(-WH_TOAST_MAX);
      toast.cd.markForCheck();
    };
    // `clear()` sans key est déjà géré par PrimeNG (tout part) ; `clear(key)` ne le serait pas.
    this.#messages.clearObserver.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(key => {
      if (!key || !toast.messages) return;
      toast.messages = toast.messages.filter(m => m.key !== key);
      toast.cd.markForCheck();
    });
  }

  ngAfterViewInit(): void {
    // PrimeNG 20 : plus de conteneur interne, l'élément hôte `<p-toast>` porte la classe `p-toast`
    // et la position en style inline.
    const container = this.toast.el?.nativeElement as HTMLElement | undefined;
    if (!container) return;
    for (const [prop, value] of Object.entries(WhToastComponent.OFFSET)) {
      this.#renderer.setStyle(container, prop, value, RendererStyleFlags2.Important);
    }
  }

  protected tone(m: ToastMessageOptions): WhToastTone {
    return toneOf(m);
  }

  protected label(m: ToastMessageOptions): string {
    return TONE_LABEL[toneOf(m)];
  }

  protected data(m: ToastMessageOptions): WhToastData {
    return (m.data ?? {}) as WhToastData;
  }

  protected runAction(action: WhToastAction, closeFn: (event: Event) => void, event: Event): void {
    action.run();
    closeFn(event);
  }
}
